// Dev-only override: lets `?marketplaceSandboxId=<id>` repoint the slide-type
// marketplace at a per-branch / per-commit Cloudflare Pages sandbox, so ONE
// presenter sandbox build can be aimed at any marketplace deploy without a
// rebuild. The id is the unique sandbox id from slide-type-creator — whatever
// `<id>.aha-slide-types-creator.pages.dev` resolves to, i.e. a branch alias
// (`aha-115-audio-quiz`) or a per-commit hash.
//
// Nothing else needs to change downstream: `/api/slide-types` synthesizes each
// entry's canvasUrl / audienceUrl / editorUrl from the request origin, so
// fetching the catalogue from the sandbox origin is enough to make the plugin
// iframes load from that same sandbox.
//
// Gated on the BUILD, via the presenter domain the build was compiled with.
// `VUE_APP_ENVAPP` cannot be used for this: it is `develop` on .env.sandbox
// AND .env.staging alike, so it can't tell those apart.
// `VUE_APP_PRESENTERDOMAIN` can — the override is honoured on the dev-facing
// builds a tester actually opens, and inert on production:
//   .env.local                    → localhost                      (override on)
//   .env.staging                  → presenter.dev.ahaslide.com     (override on)
//   .env.sandbox                  → presenter.dev.ahaslide.com     (override on)
//   .env.production / canary / EU → presenter*.ahaslides.*         (inert)
//
// Strictly URL-driven: the override is in effect ONLY while `?marketplaceSandboxId=`
// is present in the current URL. It is deliberately NOT persisted (no localStorage,
// no sticky state) — a persisted id would silently pull EVERY other presentation
// and tab onto the sandbox, contaminating unrelated tests. So the rule is simply:
// param on the link ⇒ sandbox; no param ⇒ the default marketplace. A tester who
// wants a specific presentation on the sandbox opens it with the param on the URL.
// Internal navigation that drops the param (e.g. presentation list → detail) is
// therefore back on the default marketplace, by design.

// Cloudflare Pages sandbox id: lowercase alphanumerics + dashes. Deliberately
// strict — this value becomes an iframe origin, so anything that could smuggle
// in a host ('.', '/', ':', '@') must not survive validation.
const SANDBOX_ID_REGEX = /^[a-z0-9][a-z0-9-]{0,62}$/

// Presenter domains where ?marketplaceSandboxId= is honoured — the dev-facing
// builds. Production hosts (presenter.ahaslides.com, presenter2.ahaslides.com,
// presenter.ahaslides.eu) are deliberately absent, so the override is inert
// there.
const OVERRIDE_HOSTS = ['presenter.dev.ahaslide.com']
// `local.ahaslide.com` is the loopback alias documented in .env.local.example.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', 'local.ahaslide.com']

const sandboxBaseFor = (id) => `https://${id}.aha-slide-types-creator.pages.dev`

// resolveMarketplaceBase runs once per resolved asset (one per slide type in the
// picker), so warning unconditionally would bury the message under ~100 copies
// of itself. Warn once per distinct base instead.
const warnedBases = new Set()
const warnOnce = (base) => {
  if (warnedBases.has(base)) return
  warnedBases.add(base)
  // eslint-disable-next-line no-console
  console.warn(`[marketplace override] Using sandbox catalogue ${base}`)
}

// True on the dev-facing builds (local, staging, sandbox), false on production.
// Parsed as a URL (not substring-matched) so a lookalike domain can't satisfy
// the check; the endsWith arm only accepts genuine subdomains of an allowed host.
const isOverrideAllowed = (presenterDomain) => {
  try {
    const { hostname } = new URL(presenterDomain)
    if (LOCAL_HOSTS.includes(hostname)) return true
    return OVERRIDE_HOSTS.some((h) => hostname === h || hostname.endsWith(`.${h}`))
  } catch (e) {
    return false
  }
}

/**
 * The marketplace sandbox id in effect for this page load, or '' when the
 * override is not active — i.e. a non-dev-facing build, or no valid
 * `?marketplaceSandboxId=` in the current URL. Read straight from the URL each
 * call; nothing is stored between calls.
 *
 * @param {string} presenterDomain  Config.presenterDomain — the build gate
 * @param {Location} location
 * @returns {string} the id, or '' when inactive
 */
export function activeMarketplaceSandboxId(presenterDomain, location = window.location) {
  if (!isOverrideAllowed(presenterDomain)) return ''
  const paramId = (new URLSearchParams(location.search).get('marketplaceSandboxId') || '').trim()
  return SANDBOX_ID_REGEX.test(paramId) ? paramId : ''
}

/**
 * Resolve the marketplace base URL for this page load.
 *
 * @param {string} defaultBase  Config.ahaslideMarketplaceUrl
 * @param {string} presenterDomain  Config.presenterDomain — the build gate
 * @param {Location} location
 * @returns {string} base URL with no trailing slash
 */
export function resolveMarketplaceBase(defaultBase, presenterDomain, location = window.location) {
  const stripped = (defaultBase || '').replace(/\/+$/, '')
  const id = activeMarketplaceSandboxId(presenterDomain, location)
  if (!id) return stripped

  const base = sandboxBaseFor(id)
  warnOnce(base)
  return base
}

/**
 * Append `?marketplaceSandboxId=<id>` to an audience-facing URL (the join link,
 * the QR code's payload, the participant-view iframe) — the only way a phone on a
 * separate device gets the override, since it's carried entirely on the URL.
 * Reads the id from the presenter's OWN current URL, so the audience gets the
 * sandbox precisely when the presenter is on a param-carrying URL.
 *
 * A no-op when the override is inactive, so production join links are untouched.
 * Returns the url unchanged if it isn't parseable.
 *
 * @param {string} url  an absolute audience URL
 * @param {string} presenterDomain  Config.presenterDomain — the build gate
 * @param {Location} location
 * @returns {string}
 */
export function withMarketplaceSandboxId(url, presenterDomain, location = window.location) {
  const id = activeMarketplaceSandboxId(presenterDomain, location)
  if (!id || !url) return url
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('marketplaceSandboxId', id)
    return parsed.toString()
  } catch (e) {
    return url
  }
}

/**
 * The valid sandbox id that should be carried from the current route onto the
 * next one, or '' when nothing should be. Used by a router guard to keep
 * `?marketplaceSandboxId=` visible on the URL across internal navigation (home /
 * presentation list → detail → presenting), so the override doesn't evaporate the
 * moment a link is clicked. Kept on the URL — never localStorage — so it stays
 * visible, survives F5, and a fresh tab without the param is clean.
 *
 * Returns '' when the build doesn't allow the override, when the destination
 * already carries the param (an explicit value, including an empty reset, wins),
 * or when the source id is missing/invalid.
 *
 * @param {Record<string, string|string[]>} fromQuery  Vue Router from.query
 * @param {Record<string, string|string[]>} toQuery    Vue Router to.query
 * @param {string} presenterDomain  Config.presenterDomain — the build gate
 * @returns {string} the id to inject into the destination, or ''
 */
export function marketplaceSandboxIdToPreserve(fromQuery, toQuery, presenterDomain) {
  if (!isOverrideAllowed(presenterDomain)) return ''
  if (toQuery && 'marketplaceSandboxId' in toQuery) return ''
  const raw = fromQuery && fromQuery.marketplaceSandboxId
  const value = Array.isArray(raw) ? raw[0] : raw
  const id = (value || '').trim()
  return SANDBOX_ID_REGEX.test(id) ? id : ''
}

export default resolveMarketplaceBase
